/**
 * Hooks for the trust-path member verification flow.
 *
 * Backed by /api/membership-verification-ops:
 *   GET    /pending                  — list pending registration-scope verifications
 *   POST   /:registrationId/verify    — confirm the per-registration claim
 *   POST   /:registrationId/reject    — reject the per-registration claim
 *   POST   /claim                    — self-service org-membership claim
 *   GET    /pending-memberships      — list pending org-membership claims
 *   POST   /memberships/:org/:user/approve  — approve an org-membership claim
 *   POST   /memberships/:org/:user/reject   — reject an org-membership claim
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';
import { registrationOpsKeys } from './useRegistrationOps';

export interface PendingVerification {
  registration_id: string;
  event_id: string;
  event_name: string | null;
  rodeo_id: string | null;
  rodeo_name: string | null;
  organization_id: string | null;
  user_profile_id: string;
  user_display_name: string | null;
  claimed_member_number: string | null;
  created_at: string | null;
  member_verification_status: string;
}

export interface PendingMembershipClaim {
  organization_id: string;
  organization_name: string | null;
  user_profile_id: string;
  user_display_name: string | null;
  user_email: string | null;
  member_number: string | null;
  joined_at: string | null;
  membership_status: string;
}

/**
 * One row of the profile "Organization Memberships" tab — an org the user
 * can hold a membership in, plus their current state at that org.
 */
export interface MembershipOverviewItem {
  organization_id: string;
  organization_name: string | null;
  /** Resolved signup link (membership_url, else website). Null when neither set. */
  signup_url: string | null;
  /** Whether the user already has a membership row at this org. */
  has_membership: boolean;
  member_number: string | null;
  /** 'pending' | 'approved' | null (null when no membership claimed yet). */
  membership_status: string | null;
  is_active: boolean;
  joined_at: string | null;
}

const KEY = ['membership-verification', 'pending'] as const;
const MEMBERSHIPS_KEY = ['membership-verification', 'pending-memberships'] as const;
const MY_MEMBERSHIPS_KEY = ['membership-verification', 'my-memberships'] as const;

export function usePendingMemberVerifications() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await httpClient.get<{
        data: PendingVerification[];
        total: number;
        success: boolean;
      }>('/membership-verification-ops/pending');
      return res.data ?? [];
    },
    staleTime: 30 * 1000,
  });
}

export function useVerifyMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      registrationId,
      createMembership,
    }: {
      registrationId: string;
      createMembership?: boolean;
    }) => {
      return httpClient.post<{
        registration_id: string;
        status: 'verified';
        success: boolean;
      }>(`/membership-verification-ops/${registrationId}/verify`, {
        create_membership: !!createMembership,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useRejectMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      registrationId,
      reason,
    }: {
      registrationId: string;
      reason?: string;
    }) => {
      return httpClient.post<{
        registration_id: string;
        status: 'rejected';
        non_member_fee: number;
        success: boolean;
      }>(`/membership-verification-ops/${registrationId}/reject`, {
        reason: reason && reason.trim().length > 0 ? reason.trim() : null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// ─── Org-membership trust path (self-service claim) ─────────────────────────

/**
 * Self-service: contestant claims a membership at an org by submitting a
 * member number. Backend stores the row with status='pending' and
 * is_active=true so the user is treated as a member immediately while
 * admins review the claim.
 *
 * Invalidates the registration-ops cache so any open registration modal
 * re-fetches its policy and the MembershipChoicePanel disappears.
 */
export function useClaimMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      organizationId,
      memberNumber,
    }: {
      organizationId: string;
      memberNumber: string;
    }) => {
      return httpClient.post<{
        organization_id: string;
        user_profile_id: string;
        member_number: string;
        membership_status: string;
        is_active: boolean;
        is_new: boolean;
        success: boolean;
      }>('/membership-verification-ops/claim', {
        organization_id: organizationId,
        member_number: memberNumber,
      });
    },
    onSuccess: () => {
      // Refresh registration policy (is_member flips) and outstanding
      // balance for any in-flight registration flow.
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
      qc.invalidateQueries({ queryKey: MEMBERSHIPS_KEY });
      // Refresh the profile Memberships tab so the claimed row shows as pending.
      qc.invalidateQueries({ queryKey: MY_MEMBERSHIPS_KEY });
    },
  });
}

/**
 * Data for the profile "Organization Memberships" tab — the orgs the current
 * user can hold a membership in and their current status at each.
 */
export function useMyMembershipOverview() {
  return useQuery({
    queryKey: MY_MEMBERSHIPS_KEY,
    queryFn: async () => {
      const res = await httpClient.get<{
        data: MembershipOverviewItem[];
        total: number;
        success: boolean;
      }>('/membership-verification-ops/my-memberships');
      return res.data ?? [];
    },
    staleTime: 10 * 1000,
    // Approve/reject happens in a *different* session (an admin's), so the
    // user's own claim invalidation never fires for it. Poll while the tab
    // is open + refetch on focus so a pending claim flips to approved (or
    // back to not-claimed on reject) without a manual page refresh.
    refetchInterval: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function usePendingMembershipClaims() {
  return useQuery({
    queryKey: MEMBERSHIPS_KEY,
    queryFn: async () => {
      const res = await httpClient.get<{
        data: PendingMembershipClaim[];
        total: number;
        success: boolean;
      }>('/membership-verification-ops/pending-memberships');
      return res.data ?? [];
    },
    staleTime: 30 * 1000,
  });
}

export function useApproveMembershipClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      organizationId,
      userProfileId,
      memberNumber,
    }: {
      organizationId: string;
      userProfileId: string;
      // Optional admin override of the contestant-submitted member number.
      // When provided non-empty, the backend updates the row and notifies the
      // contestant their number was corrected. Lets admins fix a typo inline
      // instead of rejecting + surcharging + refunding later.
      memberNumber?: string;
    }) => {
      const trimmed = memberNumber?.trim();
      return httpClient.post<{
        status: 'approved';
        member_number: string | null;
        member_number_changed: boolean;
        /** True when this approval reversed an earlier rejection. */
        previously_rejected: boolean;
        success: boolean;
      }>(
        `/membership-verification-ops/memberships/${organizationId}/${userProfileId}/approve`,
        trimmed ? { member_number: trimmed } : {},
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERSHIPS_KEY });
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}

export function useRejectMembershipClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      organizationId,
      userProfileId,
      reason,
    }: {
      organizationId: string;
      userProfileId: string;
      reason?: string;
    }) => {
      return httpClient.post<{
        status: 'rejected';
        success: boolean;
      }>(
        `/membership-verification-ops/memberships/${organizationId}/${userProfileId}/reject`,
        { reason: reason && reason.trim().length > 0 ? reason.trim() : null },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MEMBERSHIPS_KEY });
      qc.invalidateQueries({ queryKey: registrationOpsKeys.all });
    },
  });
}
